/**
 * Locale-aware formatters used across the courses feature.
 * Kept tiny and free of React so they can be used in tests / the API.
 */

export function formatPriceEGP(value: number, locale: 'ar' | 'en') {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-US', {
    style: 'currency',
    currency: 'EGP',
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Course duration is stored in minutes. Convert to a human-readable
 * "12h 30m" / "12س 30د" string.
 */
export function formatDuration(minutes: number | null, locale: 'ar' | 'en') {
  if (!minutes || minutes <= 0) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (locale === 'ar') {
    if (h && m) return `${h}س ${m}د`
    if (h) return `${h} ساعة`
    return `${m} دقيقة`
  }
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

/**
 * Lesson duration is stored in seconds (per the schema). Convert to
 * "MM:SS" or "H:MM:SS".
 */
export function formatLessonDuration(seconds: number | null) {
  if (!seconds || seconds <= 0) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
  return `${pad(m)}:${pad(s)}`
}

export const LEVEL_LABELS: Record<string, { ar: string; en: string }> = {
  beginner: { ar: 'مبتدئ', en: 'Beginner' },
  intermediate: { ar: 'متوسط', en: 'Intermediate' },
  advanced: { ar: 'متقدم', en: 'Advanced' },
}


/**
 * Pick the localised string from a course/lesson, falling back to the
 * Arabic primary if the English variant is missing. Centralised so we
 * can change the policy in one place (e.g. switch to "show key as
 * placeholder" for missing translations).
 */
export function pickLocalised(
  ar: string | null | undefined,
  en: string | null | undefined,
  locale: 'ar' | 'en',
): string {
  if (locale === 'en') return (en && en.trim()) ? en : (ar ?? '')
  return ar ?? ''
}
